import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, Users, Phone, MapPin, Package, RefreshCw, CheckCircle, Star } from 'lucide-react'
import { kunden as kundenApi, bexio as bexioApi } from '../../lib/api'
import { TopBar } from '../../components/layout/TopBar'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { EmptyState, LoadingSpinner } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'
import { KundeFormFields, useKundeForm } from './KundeFormPage'
import type { Kunde } from '../../types'

export function KundenListePage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<'alle' | 'favoriten'>('alle')
  const [showForm, setShowForm] = useState(false)
  const [syncResult, setSyncResult] = useState<{ added: number; updated: number; synced: number } | null>(null)

  const syncMutation = useMutation({
    mutationFn: () => bexioApi.syncKunden(),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['kunden'] })
      setSyncResult(res)
      setTimeout(() => setSyncResult(null), 6000)
    },
  })

  const { data: kundenList, isLoading } = useQuery({
    queryKey: ['kunden'],
    queryFn: () => kundenApi.list(),
  })

  const createMutation = useMutation({
    mutationFn: (data: Partial<Kunde>) => kundenApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kunden'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      setShowForm(false)
    },
  })

  const favoritMutation = useMutation({
    mutationFn: (id: string) => kundenApi.toggleFavorit(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kunden'] }),
  })

  const sorted = [...(kundenList ?? [])].sort((a, b) => (b.favorit ?? 0) - (a.favorit ?? 0))
  const filtered = sorted.filter((k) => {
    const matchSearch = [k.name, k.firma, k.ort, k.branche].some((v) =>
      v?.toLowerCase().includes(search.toLowerCase())
    )
    const matchFilter = filter === 'alle' || (filter === 'favoriten' && !!k.favorit)
    return matchSearch && matchFilter
  })

  return (
    <>
      <TopBar
        title="Kunden"
        rightAction={
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              icon={<RefreshCw size={16} className={syncMutation.isPending ? 'animate-spin' : ''} />}
              loading={syncMutation.isPending}
              onClick={() => syncMutation.mutate()}
            >
              Bexio
            </Button>
            <Button icon={<Plus size={18} />} size="sm" onClick={() => setShowForm(true)}>
              Neu
            </Button>
          </div>
        }
      />
      <div className="px-4 py-3 space-y-3">
        {syncResult && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-start gap-3">
            <CheckCircle size={18} className="text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-emerald-900 text-sm">Bexio Sync erfolgreich</p>
              <p className="text-xs text-emerald-700 mt-0.5">
                {syncResult.synced} Kontakte geprüft · {syncResult.added} neu · {syncResult.updated} aktualisiert
              </p>
            </div>
          </div>
        )}
        {syncMutation.error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-3">
            <p className="text-sm text-red-700">{(syncMutation.error as Error).message}</p>
          </div>
        )}
        <Input
          placeholder="Suche nach Name, Firma, Ort..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          icon={<Search size={16} />}
        />
        <div className="flex gap-2">
          {(['alle', 'favoriten'] as const).map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                filter === f ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600'
              }`}>
              {f === 'favoriten' && <Star size={13} className={filter === f ? 'fill-white' : 'fill-amber-400 text-amber-400'} />}
              {f === 'alle' ? 'Alle' : 'Favoriten'}
            </button>
          ))}
        </div>
        {isLoading ? (
          <LoadingSpinner />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<Users size={28} />}
            title="Keine Kunden gefunden"
            description={search ? 'Suchbegriff anpassen' : 'Ersten Kunden anlegen'}
            action={!search ? <Button onClick={() => setShowForm(true)} icon={<Plus size={16} />}>Kunde anlegen</Button> : undefined}
          />
        ) : (
          <div className="space-y-2">
            {filtered.map((kunde) => (
              <KundeCard
                key={kunde.id}
                kunde={kunde}
                onClick={() => navigate(`/kunden/${kunde.id}`)}
                onToggleFavorit={(e) => { e.stopPropagation(); favoritMutation.mutate(kunde.id) }}
              />
            ))}
          </div>
        )}
      </div>

      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title="Neuer Kunde"
        footer={
          <div className="flex gap-2">
            <Button variant="outline" fullWidth onClick={() => setShowForm(false)}>Abbrechen</Button>
            <Button
              fullWidth
              loading={createMutation.isPending}
              onClick={() => document.getElementById('kunde-form-submit')?.click()}
            >
              Speichern
            </Button>
          </div>
        }
      >
        <KundeFormInModal onSubmit={(data) => createMutation.mutate(data)} error={createMutation.error?.message} />
      </Modal>
    </>
  )
}

function KundeCard({ kunde, onClick, onToggleFavorit }: {
  kunde: Kunde
  onClick: () => void
  onToggleFavorit: (e: React.MouseEvent) => void
}) {
  const isFavorit = !!kunde.favorit
  return (
    <Card clickable onClick={onClick} className="flex items-center gap-3">
      <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${isFavorit ? 'bg-amber-100' : 'bg-blue-50'}`}>
        <span className={`font-bold text-lg ${isFavorit ? 'text-amber-600' : 'text-blue-700'}`}>
          {(kunde.firma ?? kunde.name).charAt(0).toUpperCase()}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="font-semibold text-gray-900 truncate">{kunde.firma ?? kunde.name}</p>
        </div>
        <div className="flex items-center gap-3 mt-0.5">
          {kunde.ort && (
            <span className="flex items-center gap-1 text-xs text-gray-500">
              <MapPin size={10} />{kunde.plz} {kunde.ort}
            </span>
          )}
          {kunde.telefon && (
            <span className="flex items-center gap-1 text-xs text-gray-500">
              <Phone size={10} />{kunde.telefon}
            </span>
          )}
        </div>
        {kunde.branche && <span className="text-xs text-gray-400">{kunde.branche}</span>}
      </div>
      <div className="shrink-0 flex flex-col items-end gap-1.5">
        <button
          onClick={onToggleFavorit}
          className="p-1.5 rounded-xl hover:bg-amber-50 transition-colors"
        >
          <Star size={16} className={isFavorit ? 'fill-amber-400 text-amber-400' : 'text-gray-300'} />
        </button>
        {(kunde.geraete_anzahl ?? 0) > 0 && (
          <span className="flex items-center gap-1 text-xs text-gray-400">
            <Package size={11} />{kunde.geraete_anzahl}
          </span>
        )}
      </div>
    </Card>
  )
}

function KundeFormInModal({ onSubmit, error }: { onSubmit: (data: Partial<Kunde>) => void; error?: string }) {
  const form = useKundeForm()
  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="space-y-4"
    >
      <KundeFormFields form={form} />
      {error && <p className="text-sm text-red-600">{error}</p>}
      <button id="kunde-form-submit" type="submit" className="hidden" />
    </form>
  )
}
